/**
 * @file Component Config related utility functions
 */
import _ from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import { componentsConfig } from './component-config';
import { generateSectionWithComponent } from './generate-section-with-component';
import { getPilotValue } from './pilot-utils';
import { mapWSDHoursToHours } from './time-util';
import {
  COMPONENT_TYPE_MAPPING,
  COMPONENT_TO_WEBSITE_SOURCE_DATA_MAPPING,
} from './site-config-structure';

/**
 * Filter components config by pilot value.
 * @param {Object} serverContext Server Context
 * @returns {Object} All enabled components config.
 */
export const getEnabledComponentsConfig = (serverContext = {}) => _.pick(
  componentsConfig,
  (item) => {
    if (item.pilot) {
      return getPilotValue(serverContext, item.pilot);
    }
    return true;
  }
);

/**
 * Get component name => pilot value mapping.
 * @param {Object} serverContext Server Context
 * @returns {Object} component name => pilot value mapping.
 */
export const getComponentsPilotMapping = (serverContext = {}) => _.mapObject(
  componentsConfig,
  (item) => {
    if (item.pilot) {
      return getPilotValue(serverContext, item.pilot);
    }
    return true;
  }
);


/**
 * Group the components by component type.
 * @param {Array} components All components
 * @returns {object} An Object like this:
 * {
 *  businessHours: {
 *    index: componentIndex,
 *    group: groupIndex,
 *    section: sectionIndex,
 *    all: [componentIndex, groupIndex, sectionIndex]
 *  },
 *  contact: {}
 * }
 */
export const getGroupedComponentsIndexes = (components = []) => {
  const id2Index = {};
  const componentIndexesMapping = {};

  _.each(components, (item, index) => {
    id2Index[item.id] = index;
  });

  _.each(components, (item, index) => {
    // Only deal with component
    if (item.type !== 'section' && item.type !== 'group') {
      const groupIndex = id2Index[item.parentId];
      const groupConfig = components[groupIndex];
      const sectionIndex = id2Index[groupConfig.parentId];

      if (!componentIndexesMapping[item.type]) {
        componentIndexesMapping[item.type] = {
          index,
          groupIndex,
          sectionIndex,
          sibilings: [],
          all: [sectionIndex, groupIndex, index],
        };
      } else if (groupIndex === componentIndexesMapping[item.type].groupIndex) {
        // sibiling node
        componentIndexesMapping[item.type].sibilings.push(index);
        componentIndexesMapping[item.type].all.push(index);
      }
    }
  });

  return componentIndexesMapping;
};

/**
 * Note: Update this function if there is any new logic after insert or delete component.
 * Make the components orders are correct because of new introduced component:
 * The new introduced component order may occupy the previous one, and thus it could cause
 * order collision.
 * @param {Array} components Current component configs.
 * @returns {Array} New component configs.
 */
export const refreshComponents = (components = []) => {
  const newComponents = [...components];
  const groupedComponentsIndexes = getGroupedComponentsIndexes(components);

  _.keys(groupedComponentsIndexes).forEach((key) => {
    const expectedOrder = componentsConfig[key].order;
    const { sectionIndex } = groupedComponentsIndexes[key];
    newComponents[sectionIndex].order = expectedOrder;
  });

  return newComponents;
};

/**
 * Insert a new component to current components.
 * @param {Array} components Current component configs.
 * @param {String} type New component type.
 * @returns {Object} New component configs with componentId and isExisting flag.
 */
export const insertNewComponent = (components = [], type = '') => {
  const newComponentConfig = componentsConfig[type];
  const existedComponent = _.findWhere(components, { type });

  if (_.isEmpty(newComponentConfig)) {
    return {
      components,
      cmpId: null,
      isExisting: false,
    };
  }

  if (!_.isEmpty(existedComponent)) {
    return {
      components,
      cmpId: existedComponent.id,
      isExisting: true,
    };
  }

  const { type: componentType, order } = newComponentConfig;
  const newSection = generateSectionWithComponent({
    componentType,
    sectionIndex: order,
  });
  const componentId = _.propertyOf(_.find(newSection, item => (item.type === type)))('id');
  let newComponents = [...components, ...newSection];
  newComponents = refreshComponents(newComponents);

  return {
    components: newComponents,
    cmpId: componentId,
    isExisting: false,
  };
};

/**
 * Insert a new components to current components.
 * @param {Array} components Current component configs.
 * @param {String} types New component types.
 * @returns {Array} A new components configs.
 */
export const insertNewComponents = (components = [], types = []) => {
  if (_.isEmpty(types)) {
    return components;
  }

  return _.reduce(
    types,
    (memoComponents, type) => insertNewComponent(memoComponents, type).components, components
  );
};

/**
 * Remove specific components in current components.
 * @param {Array} components Current components configs.
 * @param {Array} types A string array of component types.
 * @returns {Array} A new components configs.
 */
export const removeComponents = (components = [], types = []) => {
  if (_.isEmpty(types)) {
    return components;
  }

  const grouppedComponentsIndexes = getGroupedComponentsIndexes(components);

  const removedIndexes = [];
  _.each(types, (type) => {
    if (grouppedComponentsIndexes[type]) {
      removedIndexes.push(...(grouppedComponentsIndexes[type].all));
    }
  });

  let newComponents = _.reduce(components, (memo, value, index) => {
    if (!_.contains(removedIndexes, index)) {
      memo.push(value);
    }
    return memo;
  }, []);
  newComponents = refreshComponents(newComponents);

  return newComponents;
};

/**
 * Remove specific components data in current websiteSourcedData.
 * @param {Object} websiteSourceData website related data
 * @param {Array} types A string array of component types.
 * @returns {Object} A new websiteSourceData.
 */
export const removeComponentsDataInWebsiteSourceData = (websiteSourceData = {}, types = []) => {
  if (_.isEmpty(types)) {
    return websiteSourceData;
  }

  const newWebsiteSourceData = {
    ...websiteSourceData,
  };
  _.each(types, (type) => {
    const componentKeyInWSD = COMPONENT_TO_WEBSITE_SOURCE_DATA_MAPPING[type];
    if (componentKeyInWSD) {
      delete newWebsiteSourceData[componentKeyInWSD];
    }
  });

  return newWebsiteSourceData;
};

/**
 * Get all used component types.
 * @param {Array} components Current component configs.
 * @returns {Array} A string array of types.
 */
export const getUsedComponentTypes = (components = []) => _.chain(components)
  .filter(item => item.type !== 'section' && item.type !== 'group')
  .pluck('type')
  .value();

/**
* Get all used optional component types.
* @param {Array} components Current component configs.
* @returns {Array} A string array of types.
*/
export const getUsedOptionalComponentTypes = (components = []) => {
  const usedCompTypes = getUsedComponentTypes(components);
  return _.filter(usedCompTypes, type => !componentsConfig[type].required);
};

const getBusinessHoursFromWebsiteSourceData = (websiteSourceData = {}) => {
  if (!_.isEmpty(websiteSourceData.BusinessHours)) {
    return mapWSDHoursToHours(websiteSourceData.BusinessHours);
  }

  return null;
};

/**
 * Some components config needs to be expanded based on the websiteSourceData.
 * For those not in here, default values would be used.
 * Note: This method always return a complete mapping, the hidden logic of a certain
 * component leave inside relative component!
 * @param {Object} websiteSourceData website related data
 * @param {Object} serverContext Server Context
 * @returns {Object} Overrided components configs
 * {
 *  introduction: {*},
 *  header: {*}
 * }
 */
export const getOverrideCompConfigMapping = (websiteSourceData = {}) => {
  const componentsMapping = {
    introduction: {
      headline: websiteSourceData.BusinessName,
      description: websiteSourceData.Description,
    },
    coverImage: {
      imageId: websiteSourceData.CoverImageId,
      imageName: websiteSourceData.CoverImageId ? null : 'consultant.jpg',
      imageWidth: null,
      imageHeight: null,
    },
    contact: {
      businessHours: getBusinessHoursFromWebsiteSourceData(websiteSourceData),
      showEmail: !_.isEmpty(websiteSourceData.Email),
      showPhone: !_.isEmpty(websiteSourceData.PhoneNumber),
      showLocation: !_.isEmpty(websiteSourceData.SingleLineAddress),
      showHours: !_.isEmpty(websiteSourceData.BusinessHours),
    },
    businessHours: {
      businessHours: getBusinessHoursFromWebsiteSourceData(websiteSourceData),
    },
  };

  // Do not add optional section if data is empty.
  if (!_.isEmpty(websiteSourceData.GalleryImageIds)) {
    componentsMapping.gallery = {
      photos: _.map(websiteSourceData.GalleryImageIds, imageId => ({ imageId })),
    };
  }

  if (!_.isEmpty(websiteSourceData.SocialUrls)) {
    componentsMapping.socialLinks = _.keys(websiteSourceData.SocialUrls).reduce((obj, key) => {
      const value = websiteSourceData.SocialUrls[key];
      const keyName = `${key.toLowerCase()}Link`;

      return {
        ...obj,
        [keyName]: {
          isDisplay: !!value,
          value,
        },
      };
    }, {});
  }

  if (!_.isEmpty(websiteSourceData.Description)) {
    componentsMapping.summary = {
      businessSummary: websiteSourceData.Description,
    };
  }

  return componentsMapping;
};

/**
 * If a pilot for components is off, remove related component configs in Pages.
 * @param {Array} components Current complete components config
 * @param {Object} enabledComponentsConfig Enabled component configs
 * @returns {Array} New components config after removing disabled components
 */
export const removeDisabledComponents = (components = [], enabledComponentsConfig = {}) => {
  const allUsedComponentTypes = getUsedComponentTypes(components);

  // Pick component types that had been created but not available now because of close pilot.
  const removedComponentTypes = _.difference(
    allUsedComponentTypes,
    _.keys(enabledComponentsConfig)
  );
  const newComponents = removeComponents(components, removedComponentTypes);

  return newComponents;
};


/**
 * In some smart page, there are multiple components for the same type in the site config.
 * Not sure how it is generated, but for now every component should only be showed once.
 * https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/4234631
 *
 * @param {Array} components Current component configs.
 * @returns {Array} New component configs.
 */
export const removeDuplicatedComponents = (components) => {
  const newComponents = [...components];
  const groupedComponentsIndexes = getGroupedComponentsIndexes(components);
  const usedType = {};

  const cmpTypes = getUsedComponentTypes(components);

  _.each(cmpTypes, (type) => {
    if (usedType[type] && _.isEmpty(groupedComponentsIndexes[type].sibilings)) {
      const { all } = groupedComponentsIndexes[type];

      _.each(all, (index) => {
        newComponents[index] = null;
      });
    } else {
      usedType[type] = true;
    }
  });

  return _.compact(newComponents);
};

/**
 * If add new component type, using this function to generate new component configs from
 * existing smart page data.
 * Example: If a user does not have a Independent Business Hours Component, generate a new one
 * from existing Contact Component.
 *
 * If there are more new components, consider to create a transform config file.
 * @param {Array} components Current complete component configs
 * @param {Object} enabledComponentsConfig Enabled component configs
 * @returns {Array} New components config after generate new components.
 */
export const generateNewCmpsFromExistingData = (components = [], enabledComponentsConfig = {}) => {
  // Independent Business Hours section
  const isBusinessHoursEnabled = !!enabledComponentsConfig[COMPONENT_TYPE_MAPPING.BusinessHours];
  let newComponents = [...components];
  const contactComponentConfig = _.find(
    newComponents,
    item => item.type === COMPONENT_TYPE_MAPPING.Contact
  );

  if (isBusinessHoursEnabled) {
    // Handle "showHours" logic:
    // According to current logic, if the showHours is true or undefined,
    // they both mean the user is using Business hours. The explicit false value means
    // the user closed the business hours.
    if (contactComponentConfig.showHours === false) {
      newComponents = removeComponents(newComponents, [COMPONENT_TYPE_MAPPING.BusinessHours]);
    } else {
      // whether the Business Hour Section exists is handled inside the insertNewComponent function.
      newComponents = insertNewComponent(
        components,
        COMPONENT_TYPE_MAPPING.BusinessHours
      ).components;

      const businessHoursComponentIndex = _.findIndex(
        newComponents,
        { type: COMPONENT_TYPE_MAPPING.BusinessHours }
      );
      newComponents[businessHoursComponentIndex].businessHours = _.map(
        contactComponentConfig.businessHours,
        day => cloneDeep(day)
      );
    }
  }

  return newComponents;
};
